import _, { Dictionary } from 'lodash';
import {
  getCurrentSecurityGroup,
  getSecurityGroupsState,
  getSubscriberSecurityGroupState,
} from '../features/security/selectors';
import { getTemplateId, getTemplates } from '../models/api/templates';
import store from '../store/index';
import {
  DocumentTemplateType,
  INotification,
  IProjectUser,
  ISimplifiedProjectView,
  IUser,
  SecurityPermissionLevel,
  TaskManagerPermission,
} from '../api-client/autogenerated';
import { getDocumentsType } from '../features/documents/selectors';
import { ManagePermissionsDialogType } from '../main-components/design/ManagePermissionsDialog';
import { getProjectState, getProjectUsersState } from '../features/project/selectors';
import { canViewDesign, getProjectUsers } from './utils';
import { getUserState } from '../features/user/selectors';

let templates: Dictionary<string> | null = null;
try {
  setTimeout(() => getTemplates().then((t) => (templates = t)), 100);
} catch (error: any) {
  console.log(error);
}

/**
 * Whether the project currently open is archived, and so view-only for everyone -- subscriber and
 * site admins included, since the API refuses their writes too. Read straight off the store so
 * the plain (non-hook) permission helpers below can consult it.
 */
export const isCurrentProjectArchived = () => !!store.getState().project.project?.isArchived;

/**
 * The two document permission levels below are what nearly every edit affordance in the portal
 * gates on -- add, edit, delete, review, comment. Clamping them to VIEW on an archived project is
 * what turns the whole document engine read-only in one place, and it has to happen ahead of the
 * admin short-circuit: an archived project is view-only for admins as well.
 *
 * VIEW is 0, so callers testing `permission >= CREATE` stop offering the action while callers
 * testing for a non-null permission still render the page. That is the intent: the project stays
 * legible, it just cannot be changed.
 */
export const getDocPermission = () => {
  const state = store.getState();
  const user = getUserState(state);
  if (isCurrentProjectArchived()) return SecurityPermissionLevel.NUMBER_0;
  if (hasAdminPermissions()) return SecurityPermissionLevel.NUMBER_4;
  const documentPermission = state.document.permissions.find((p) => p.userId === user.id)
    ?.securityPermissionLevel;
  const documentTypePermission = getDocumentTypePermission();

  return Math.max(documentPermission ?? 0, documentTypePermission ?? 0);
};

export const getDocumentTypePermission = () => {
  const state = store.getState();
  const security = getCurrentSecurityGroup(state);
  if (isCurrentProjectArchived()) return SecurityPermissionLevel.NUMBER_0;
  if (hasAdminPermissions()) return SecurityPermissionLevel.NUMBER_4;
  if (!templates || !security || !state.documents?.type) return null;
  return security.securityGroupDocumentTemplateList?.find(
    (group) => group.documentTemplateId === templates![state.documents?.type!],
  )?.securityPermissionLevel;
};

/**
 * The current user's task-manager permission (None/Personal/Company/Everyone/Admin) for a given
 * task document type, from the current security group. Returns null when unknown. This is the
 * real task visibility/authority field — do NOT use the shadow securityPermissionLevel for
 * task-specific decisions such as delete.
 */
export const getTaskManagerPermissionForType = (documentType?: DocumentTemplateType | null) => {
  if (!documentType) return null;
  const state = store.getState();
  const security = getCurrentSecurityGroup(state);
  if (!templates || !security) return null;
  return (
    security.securityGroupDocumentTemplateList?.find(
      (group) => group.documentTemplateId === templates![documentType],
    )?.taskManagerPermission ?? null
  );
};

/**
 * Whether the current user may delete a document of the given type. Task / CA Task deletion
 * requires Admin-level task-manager permission (matching the backend canDeleteTask); all other
 * types require ALL. Site/subscriber admins may always delete.
 */
export const canDeleteDocumentType = (documentType?: DocumentTemplateType | null) => {
  // Ahead of the admin short-circuit: nothing is deletable in an archived project.
  if (isCurrentProjectArchived()) return false;
  if (hasAdminPermissions()) return true;
  if (documentType === DocumentTemplateType.Task || documentType === DocumentTemplateType.CaTask) {
    return getTaskManagerPermissionForType(documentType) === TaskManagerPermission.NUMBER_4;
  }
  return (getDocPermission() ?? 0) >= SecurityPermissionLevel.NUMBER_4;
};

export const hasAdminPermissions = (user?: IUser | null) => {
  const state = store.getState();
  const userTemp = user ? user : state.user.user;
  if (userTemp) {
    if (userTemp.isSiteAdmin) return true;
    if (userTemp.adminOfSubscriberId) {
      if (!state.project.project) return false;
      return userTemp.adminOfSubscriberId === state.project.project.subscriberId;
    }
  }
  return false;
};

export const hasSearchPermissions = (
  projects: ISimplifiedProjectView[] | undefined,
  currentProjectId?: string,
) => {
  if (hasAdminPermissions()) return true;

  if (!projects?.length) return false;

  if (currentProjectId) {
    return projects.some((p) => p.id === currentProjectId && p.canSearch);
  }

  return projects.some((p) => p.canSearch);
};

export const isLoggedIn = () => {
  const state = store.getState();
  if (state.user) {
    if (state.user.user) {
      if (state.user.user.id !== '') return true;
    }
  }
  return false;
};

export const hasCompletedConstructionSetup = () => {
  const state = store.getState();
  if (state.project?.project?.architectAdministratorUserId) {
    return true;
  }
  return false;
};

export const getProjectUserPermissions = () => {
  const state = store.getState();
  if (
    !state.project.project?.projectUserList ||
    !state.document.document ||
    state.document.permissions.length === 0
  )
    return [];
  return getProjectUsers(state.project.project, false).map((projectUser) => {
    const permission = state.document.permissions.find(
      ({ userId }) => userId === projectUser.userId,
    )?.securityPermissionLevel;
    return { projectUser, permission };
  });
};

export const getProjectUserPermissionsForAdditionalReview = async () => {
  const state = store.getState();
  if (
    !state.project.project?.projectUserList ||
    !state.document.document ||
    state.security.subscriberSecurity.length === 0
  )
    return [];
  const additionalReviewTemplateId = templates
    ? templates[DocumentTemplateType.AdditionalReview]
    : await getTemplateId(DocumentTemplateType.AdditionalReview);
  return getProjectUsers(state.project.project, false).map((projectUser) => {
    let permission: SecurityPermissionLevel | null | undefined;
    if (hasAdminPermissions(projectUser.user)) {
      permission = SecurityPermissionLevel.NUMBER_4;
    } else {
      permission = state.security.subscriberSecurity
        .find((s) => s.id === projectUser.securityGroupId)
        ?.securityGroupDocumentTemplateList?.find(
          (list) => list.documentTemplateId === additionalReviewTemplateId,
        )?.securityPermissionLevel;
    }
    return { projectUser, permission };
  });
};

export const getUserPermissionForCurrentDocType = (
  pUser: IProjectUser,
  /**
   * Checks permission against this document type instead of the currently viewed
   * document (Redux documents.type). Needed by callers previewing a notification list
   * for a document type other than the one currently on screen — e.g. the Meeting
   * Minutes task review dialog previews Task/CA Task permissions while still on the
   * Meeting Minutes document.
   */
  docTypeOverride?: DocumentTemplateType,
) => {
  const state = store.getState();
  const docType = docTypeOverride ?? getDocumentsType(state);
  if (!docType) return SecurityPermissionLevel.NUMBER_4;
  if (!templates) return null;
  const templateId = templates[docType];
  const allSecurityGroups = _.uniqBy(
    [...getSubscriberSecurityGroupState(state), ...getSecurityGroupsState(state)],
    (group) => group.id,
  );
  const security = allSecurityGroups
    .find((group) => group.id === pUser.securityGroupId)
    ?.securityGroupDocumentTemplateList?.find((list) => list.documentTemplateId === templateId);
  return security?.securityPermissionLevel;
};

export const hasPermissionToView = (
  type: ManagePermissionsDialogType,
  user?: IProjectUser | IUser | null,
  /** See getUserPermissionForCurrentDocType's docTypeOverride. */
  docTypeOverride?: DocumentTemplateType,
): boolean | null | undefined => {
  if (
    type === ManagePermissionsDialogType.None ||
    type === ManagePermissionsDialogType.PartiesPresent
  ) {
    return true;
  }

  const userTemp = user as IUser;
  const state = store.getState();
  const project = getProjectState(state);
  const projectUsers = getProjectUsersState(state);
  if (type === ManagePermissionsDialogType.Design) {
    //user parameter can be assumed to be of type IUser.
    return (
      user &&
      (userTemp.adminOfSubscriberId === project?.subscriberId ||
        userTemp.isSiteAdmin ||
        canViewDesign(
          project?.subscriber?.productPackage,
          projectUsers.find(({ userId }) => userId === userTemp.id)?.securityGroup,
        ))
    );
  } else if (type === ManagePermissionsDialogType.AssociatedUsers) {
    //If user is of type IUser, find the IProjectUser object in projectUsers. Otherwise, just keep using user.
    const pUser: IProjectUser | undefined = user!.hasOwnProperty('userId')
      ? (user as IProjectUser)
      : projectUsers.find((x) => x.userId === user!.id);
    if (pUser) {
      const permission = getUserPermissionForCurrentDocType(pUser, docTypeOverride);
      return (permission !== null && permission !== undefined) || hasAdminPermissions(pUser.user!);
    }
    return false;
  } else if (type === ManagePermissionsDialogType.ProjectLifecycle) {
    const lifecyclePermission = getCurrentSecurityGroup(state)?.projectLifecyclePermission;
    return (
      (lifecyclePermission !== null && lifecyclePermission !== undefined) ||
      userTemp?.isSiteAdmin ||
      userTemp?.adminOfSubscriberId === project?.subscriberId
    );
  }

  return true;
};

export const getProjectNameFromNotification = (notification?: INotification | null) => {
  if (!notification) return undefined;
  return (
    notification.project?.name ||
    notification.document?.project?.name ||
    store
      .getState()
      .projects.projects.find(
        (p) =>
          p.id ===
          (notification.projectId ||
            notification.document?.projectId ||
            notification.designFile?.projectId ||
            notification.designFileShare?.designFile?.projectId),
      )?.name
  );
};

export const userIsOnProject = (projectUser: IProjectUser | undefined | null, user: IUser) => {
  if (!projectUser) return false;

  return hasAdminPermissions(user) || !!projectUser.securityGroupId;
};
